import React, { useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Upload, Music, Check, X, FileAudio, Loader2, Cloud, HardDrive } from 'lucide-react';
import { motion } from 'framer-motion';
import { adminFetch, isAdminAuthenticated } from '@/utils/adminAuth';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// Cloud storage settings
const CLOUD_URL = 'https://cloud.casasamba.com';
const CLOUD_API_KEY = 'TWVzdHJlIEN1cnRpczpNRGU6ZGE/PmNJIU8x';

export const AdminBulkWavUpload = () => {
  const navigate = useNavigate();
  const [tracks, setTracks] = useState([]);
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [matches, setMatches] = useState({});
  const [uploadDestination, setUploadDestination] = useState('server'); // 'server' or 'cloud'
  const [cloudFolder, setCloudFolder] = useState('wav-uploads');
  const [cloudFolders, setCloudFolders] = useState([]);

  // Load tracks on mount
  React.useEffect(() => {
    if (!isAdminAuthenticated()) {
      navigate('/admin');
      return;
    }
    loadTracks();
    loadCloudFolders();
  }, []);

  const loadTracks = async () => {
    try {
      const res = await axios.get(`${API}/api/tracks`);
      setTracks(res.data);
    } catch (error) {
      toast.error('Failed to load tracks');
    }
  };

  const loadCloudFolders = async () => {
    try {
      const res = await fetch(`${CLOUD_URL}/index.php?action=folders&api_key=${encodeURIComponent(CLOUD_API_KEY)}`);
      const data = await res.json();
      if (data.folders) {
        setCloudFolders(data.folders.filter(f => f !== ''));
      }
    } catch (error) {
      console.log('Could not load cloud folders');
    }
  };

  // Match WAV filename to track
  const findMatchingTrack = (filename) => {
    // Remove .wav extension and clean up filename
    const cleanName = filename
      .replace(/\.wav$/i, '')
      .replace(/[-_]/g, ' ')
      .toLowerCase()
      .trim();

    // Try to find a matching track
    for (const track of tracks) {
      const trackTitle = track.title.toLowerCase().trim();
      const trackAudioName = (track.audio_url || '')
        .split('/')
        .pop()
        .replace(/\.mp3$/i, '')
        .replace(/[-_]/g, ' ')
        .toLowerCase();

      // Check if filename contains track title or vice versa
      if (
        cleanName.includes(trackTitle) ||
        trackTitle.includes(cleanName) ||
        cleanName.includes(trackAudioName) ||
        trackAudioName.includes(cleanName) ||
        cleanName === trackAudioName
      ) {
        return track;
      }
    }
    return null;
  };

  const handleFileSelect = (e) => {
    const selectedFiles = Array.from(e.target.files).filter(f => 
      f.name.toLowerCase().endsWith('.wav')
    );

    if (selectedFiles.length === 0) {
      toast.error('Please select WAV files');
      return;
    }

    // Auto-match files to tracks
    const newMatches = {};
    selectedFiles.forEach(file => {
      const match = findMatchingTrack(file.name);
      if (match) {
        newMatches[file.name] = match.id;
      }
    });

    setFiles(selectedFiles);
    setMatches(newMatches);
    
    const matchedCount = Object.keys(newMatches).length;
    toast.success(`Found ${matchedCount} of ${selectedFiles.length} matches`);
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    const droppedFiles = Array.from(e.dataTransfer.files).filter(f =>
      f.name.toLowerCase().endsWith('.wav')
    );

    if (droppedFiles.length === 0) {
      toast.error('Please drop WAV files');
      return;
    }

    const newMatches = {};
    droppedFiles.forEach(file => {
      const match = findMatchingTrack(file.name);
      if (match) {
        newMatches[file.name] = match.id;
      }
    });

    setFiles(droppedFiles);
    setMatches(newMatches);

    const matchedCount = Object.keys(newMatches).length;
    toast.success(`Found ${matchedCount} of ${droppedFiles.length} matches`);
  }, [tracks]);

  const handleMatchChange = (filename, trackId) => {
    setMatches(prev => ({
      ...prev,
      [filename]: trackId
    }));
  };

  // Upload to cloud storage
  const uploadToCloud = async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    if (cloudFolder) {
      formData.append('folder', cloudFolder);
    }

    const response = await fetch(`${CLOUD_URL}/index.php?action=upload&api_key=${encodeURIComponent(CLOUD_API_KEY)}`, {
      method: 'POST',
      body: formData
    });

    return response.json();
  };

  // Upload all files (to server or cloud)
  const uploadAll = async () => {
    const filesToUpload = uploadDestination === 'server' 
      ? files.filter(f => matches[f.name])
      : files; // For cloud, upload all files
    
    if (filesToUpload.length === 0) {
      toast.error(uploadDestination === 'server' ? 'No files matched to tracks' : 'No files to upload');
      return;
    }

    setUploading(true);
    let successCount = 0;

    for (const file of filesToUpload) {
      setUploadProgress(prev => ({ ...prev, [file.name]: 'uploading' }));

      try {
        if (uploadDestination === 'cloud') {
          // Upload directly to DreamHost cloud
          const result = await uploadToCloud(file);
          if (result.success) {
            setUploadProgress(prev => ({ ...prev, [file.name]: 'success' }));
            successCount++;
          } else {
            throw new Error(result.error || 'Upload failed');
          }
        } else {
          // Upload to server and link to track
          const trackId = matches[file.name];
          const formData = new FormData();
          formData.append('file', file);

          const uploadRes = await adminFetch('/admin/upload/audio', {
            method: 'POST',
            body: formData
          });
          const uploadData = await uploadRes.json();

          // Update track with WAV URL
          await adminFetch(`/admin/tracks/${trackId}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ wav_url: uploadData.url })
          });

          setUploadProgress(prev => ({ ...prev, [file.name]: 'success' }));
          successCount++;
        }
      } catch (error) {
        console.error(`Failed to upload ${file.name}:`, error);
        setUploadProgress(prev => ({ ...prev, [file.name]: 'error' }));
      }
    }

    setUploading(false);
    toast.success(`Uploaded ${successCount} WAV files!`);
  };

  const getMatchedTrackTitle = (filename) => {
    const trackId = matches[filename];
    if (!trackId) return null;
    const track = tracks.find(t => t.id === trackId);
    return track?.title;
  };

  // Prevent browser from opening dropped files globally (Safari compatibility)
  React.useEffect(() => {
    const preventDefaults = (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (e.dataTransfer) {
        e.dataTransfer.dropEffect = 'none';
      }
      return false;
    };
    
    const events = ['dragenter', 'dragover', 'dragleave', 'drop'];
    events.forEach(eventName => {
      document.addEventListener(eventName, preventDefaults, { capture: true });
      document.body.addEventListener(eventName, preventDefaults, { capture: true });
    });
    
    return () => {
      events.forEach(eventName => {
        document.removeEventListener(eventName, preventDefaults, { capture: true });
        document.body.removeEventListener(eventName, preventDefaults, { capture: true });
      });
    };
  }, []);

  return (
    <div className="min-h-screen bg-stone-900 text-white">
      {/* Header */}
      <div className="bg-stone-800 border-b border-stone-700 px-6 py-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/admin/music" className="p-2 hover:bg-stone-700 rounded-lg">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div>
              <h1 className="text-2xl font-bold">Bulk WAV Upload</h1>
              <p className="text-sm text-muted-foreground">Upload multiple WAV files at once</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-8">
        {/* Destination Selector */}
        <div className="bg-stone-800 rounded-xl p-6 border border-stone-700 mb-6">
          <h2 className="font-bold text-lg mb-4">Upload Destination</h2>
          <div className="flex gap-4">
            <button
              onClick={() => setUploadDestination('server')}
              className={`flex-1 p-4 rounded-xl border-2 transition-all ${
                uploadDestination === 'server' 
                  ? 'border-primary bg-primary/10' 
                  : 'border-stone-700 hover:border-stone-600'
              }`}
            >
              <HardDrive className={`w-8 h-8 mx-auto mb-2 ${uploadDestination === 'server' ? 'text-primary' : 'text-stone-500'}`} />
              <p className="font-bold">App Server</p>
              <p className="text-xs text-muted-foreground">Link to existing tracks</p>
            </button>
            <button
              onClick={() => setUploadDestination('cloud')}
              className={`flex-1 p-4 rounded-xl border-2 transition-all ${
                uploadDestination === 'cloud' 
                  ? 'border-cyan-500 bg-cyan-500/10' 
                  : 'border-stone-700 hover:border-stone-600'
              }`}
            >
              <Cloud className={`w-8 h-8 mx-auto mb-2 ${uploadDestination === 'cloud' ? 'text-cyan-400' : 'text-stone-500'}`} />
              <p className="font-bold">Personal Cloud</p>
              <p className="text-xs text-muted-foreground">cloud.casasamba.com</p>
            </button>
          </div>

          {uploadDestination === 'cloud' && (
            <div className="mt-4">
              <label className="text-sm font-medium mb-2 block">Upload to folder:</label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={cloudFolder}
                  onChange={(e) => setCloudFolder(e.target.value)}
                  placeholder="Folder name (optional)"
                  className="flex-1 bg-stone-700 border border-stone-600 rounded-lg px-4 py-2"
                />
                {cloudFolders.length > 0 && (
                  <select
                    onChange={(e) => setCloudFolder(e.target.value)}
                    className="bg-stone-700 border border-stone-600 rounded-lg px-3"
                  >
                    <option value="">Select existing...</option>
                    {cloudFolders.map(f => (
                      <option key={f} value={f}>{f}</option>
                    ))}
                  </select>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Instructions */}
        <div className="bg-stone-800 rounded-xl p-6 border border-stone-700 mb-8">
          <h2 className="font-bold text-lg mb-3">How it works:</h2>
          <ol className="list-decimal list-inside space-y-2 text-muted-foreground">
            {uploadDestination === 'server' ? (
              <>
                <li>Drag & drop or select your WAV files</li>
                <li>System auto-matches filenames to existing tracks</li>
                <li>Review/adjust matches if needed</li>
                <li>Click "Upload All" to upload matched files</li>
              </>
            ) : (
              <>
                <li>Drag & drop or select your WAV/audio files</li>
                <li>Files will upload directly to your personal cloud</li>
                <li>Access them anytime from Mobile Studio or cloud.casasamba.com</li>
              </>
            )}
          </ol>
          <p className="mt-4 text-sm text-amber-400">
            {uploadDestination === 'server' 
              ? 'Tip: Name your WAV files similar to track titles for better auto-matching'
              : 'Tip: Create folders to organize your audio library'
            }
          </p>
        </div>

        {/* Drop Zone */}
        <div
          onDrop={handleDrop}
          onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); e.dataTransfer.dropEffect = 'copy'; }}
          onDragEnter={(e) => { e.preventDefault(); e.stopPropagation(); e.currentTarget.classList.add('border-primary', 'bg-primary/10'); }}
          onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); e.currentTarget.classList.remove('border-primary', 'bg-primary/10'); }}
          className="border-2 border-dashed border-stone-600 rounded-xl p-12 text-center hover:border-primary transition-colors cursor-pointer mb-8"
          onClick={() => document.getElementById('wav-input').click()}
        >
          <input
            id="wav-input"
            type="file"
            accept={uploadDestination === 'cloud' ? '.wav,.mp3,.m4a,.webm,.mp4' : '.wav'}
            multiple
            onChange={handleFileSelect}
            className="hidden"
          />
          <Upload className="w-12 h-12 mx-auto mb-4 text-stone-500" />
          <p className="text-lg font-medium mb-2">
            {uploadDestination === 'cloud' ? 'Drop audio/video files here' : 'Drop WAV files here'}
          </p>
          <p className="text-sm text-muted-foreground">or click to browse</p>
        </div>

        {/* Files List */}
        {files.length > 0 && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-bold">
                {files.length} files selected {uploadDestination === 'server' && `• ${Object.keys(matches).length} matched`}
              </h3>
              <button
                onClick={uploadAll}
                disabled={uploading || (uploadDestination === 'server' && Object.keys(matches).length === 0)}
                className="px-6 py-3 bg-primary text-black font-bold rounded-lg hover:bg-primary/90 disabled:opacity-50 flex items-center gap-2"
              >
                {uploading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="w-5 h-5" />
                    Upload All ({Object.keys(matches).length})
                  </>
                )}
              </button>
            </div>

            <div className="space-y-2">
              {files.map((file) => {
                const status = uploadProgress[file.name];
                const matchedTitle = getMatchedTrackTitle(file.name);

                return (
                  <div
                    key={file.name}
                    className={`p-4 rounded-xl border ${
                      status === 'success' ? 'bg-green-500/10 border-green-500/30' :
                      status === 'error' ? 'bg-red-500/10 border-red-500/30' :
                      matches[file.name] ? 'bg-stone-800 border-stone-700' :
                      'bg-stone-800/50 border-stone-700/50'
                    }`}
                  >
                    <div className="flex items-center gap-4">
                      {/* Status Icon */}
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                        status === 'success' ? 'bg-green-500/20' :
                        status === 'error' ? 'bg-red-500/20' :
                        status === 'uploading' ? 'bg-primary/20' :
                        'bg-purple-500/20'
                      }`}>
                        {status === 'success' ? <Check className="w-5 h-5 text-green-500" /> :
                         status === 'error' ? <X className="w-5 h-5 text-red-500" /> :
                         status === 'uploading' ? <Loader2 className="w-5 h-5 text-primary animate-spin" /> :
                         <FileAudio className="w-5 h-5 text-purple-400" />}
                      </div>

                      {/* File Info */}
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{file.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {(file.size / 1024 / 1024).toFixed(1)} MB
                        </p>
                      </div>

                      {/* Match Selector */}
                      <div className="flex-shrink-0">
                        {status === 'success' ? (
                          <span className="text-green-500 font-medium">Uploaded!</span>
                        ) : status === 'error' ? (
                          <span className="text-red-500 font-medium">Failed</span>
                        ) : (
                          <select
                            value={matches[file.name] || ''}
                            onChange={(e) => handleMatchChange(file.name, e.target.value)}
                            className="bg-stone-700 border border-stone-600 rounded-lg px-3 py-2 text-sm max-w-[200px]"
                          >
                            <option value="">-- Select Track --</option>
                            {tracks.map(track => (
                              <option key={track.id} value={track.id}>
                                {track.title}
                              </option>
                            ))}
                          </select>
                        )}
                      </div>
                    </div>

                    {matchedTitle && !status && (
                      <p className="mt-2 text-sm text-green-400 flex items-center gap-1">
                        <Check className="w-4 h-4" />
                        Auto-matched to: {matchedTitle}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* No files state */}
        {files.length === 0 && (
          <div className="text-center py-12 text-muted-foreground">
            <Music className="w-16 h-16 mx-auto mb-4 opacity-50" />
            <p>No files selected yet</p>
            <p className="text-sm">Drop WAV files above or click to browse</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminBulkWavUpload;
